import { useEffect, useRef } from 'react';

/** `?domain=` is sent this long after the website question shows. */
export const AUTO_SEND_MS = 600;

export interface UseDomainAutoSendOptions {
  /** The `?domain=` value, if any. */
  domain: string | null;
  /** The session already holds a site: never overwrite it. */
  hasUrl: boolean;
  /** The website question is what the chat is asking and it is on screen. */
  askingForUrl: boolean;
  send: (domain: string) => void;
}

/**
 * Sends a `?domain=` prefill once, shortly after the website question shows, and only for a session that has
 * no site yet. A resumed session, or a person who already answered, is left alone.
 */
export function useDomainAutoSend({ domain, hasUrl, askingForUrl, send }: UseDomainAutoSendOptions): void {
  const sent = useRef(false);
  useEffect(() => {
    if (!domain || sent.current || hasUrl || !askingForUrl) return;
    const timer = window.setTimeout(() => {
      sent.current = true;
      send(domain);
    }, AUTO_SEND_MS);
    return () => window.clearTimeout(timer);
  }, [domain, hasUrl, askingForUrl, send]);
}
